import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Badge
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { stockReturnApi } from 'api/endpoints/StockReturn';
import { categoryApi } from 'api/endpoints/Category';
import { getTenantFromToken } from 'utils/routeUtils';

// ─── helpers ───────────────────────────────────────────────────────────────────
const formatUGX = val => `${Math.round(Number(val) || 0).toLocaleString()} UGX`;

const newItem = () => ({
  tempId: Date.now() + Math.random(),
  productCategoryId: '',
  productId: '',
  optionId: '',
  sku: '',
  lotId: '',
  availableQty: '',
  returnQty: '',
  uom: '',
  pricePerUnit: '',
  stockValue: 0,
  productList: [],
  optionsList: [],
  lotsList: []
});

// ─── component ─────────────────────────────────────────────────────────────────
export default function CreateStockReturn() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();

  // ── header state ─────────────────────────────────────────────────────────────
  const [returnDate, setReturnDate] = useState(
    new Date().toISOString().substring(0, 10)
  );
  const [warehouseId, setWarehouseId] = useState('');
  const [poInvoiceNo, setPoInvoiceNo] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [refNo, setRefNo] = useState('');
  const [notes, setNotes] = useState('');
  const [taxPercent, setTaxPercent] = useState(0);
  const [stockReturnCode, setStockReturnCode] = useState('Loading...');
  const [nextCodeLoading, setNextCodeLoading] = useState(false);
  const [status, setStatus] = useState('draft');

  // ── dropdown options ──────────────────────────────────────────────────────────
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);

  // ── line items ────────────────────────────────────────────────────────────────
  const [items, setItems] = useState([newItem()]);

  // ── UI state ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── CATEGORY BADGE COLOURS ────────────────────────────────────────────────────
  const CATEGORY_COLOURS = [
    'primary',
    'success',
    'warning',
    'danger',
    'info',
    'secondary'
  ];
  const catColour = useCallback(
    catId => {
      const idx = categories.findIndex(c => c.value === catId);
      return CATEGORY_COLOURS[idx % CATEGORY_COLOURS.length] || 'secondary';
    },
    [categories]
  );

  // ── load master data ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const requests = [
          stockReturnApi.getWarehouses(),
          stockReturnApi.getSuppliers(),
          stockReturnApi.getCustomers(),
          categoryApi.getCategoryList()
        ];
        // Fetch next code preview only for new forms
        if (!isEditMode) {
          setNextCodeLoading(true);
          requests.push(stockReturnApi.getNextReturnCode());
        }
        const results = await Promise.all(requests);
        const [whRes, supRes, custRes, catRes] = results;
        setWarehouses(whRes.data?.results || whRes.data || []);
        setSuppliers(supRes.data?.results || supRes.data || []);
        setCustomers(custRes.data?.results || custRes.data || []);
        setCategories(catRes.data?.results || []);
        if (!isEditMode && results[4]) {
          setStockReturnCode(results[4].data?.results?.next_code || '');
        } else if (!isEditMode) {
          setStockReturnCode('');
        }
      } catch (err) {
        console.error('Failed to load master data:', err);
        toast.error('Failed to load form dropdowns');
      } finally {
        setNextCodeLoading(false);
      }
    };
    fetchMasterData();
  }, [isEditMode]);

  // ── load existing record for edit ─────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode) return;
    const fetchRecord = async () => {
      setLoading(true);
      try {
        const res = await stockReturnApi.getStockReturn(id);
        const data = res.data?.results;
        if (!data) return;

        setStockReturnCode(data.stock_return_code || '');
        setReturnDate(
          data.return_date || new Date().toISOString().substring(0, 10)
        );
        setWarehouseId(data.warehouse_id || '');
        setPoInvoiceNo(data.po_invoice_no || '');
        setSupplierId(data.supplier_id || '');
        setCustomerId(data.customer_id || '');
        setRefNo(data.ref_no || '');
        setNotes(data.notes || '');
        setTaxPercent(data.tax_percent || 0);
        setStatus(data.status || 'draft');

        if (data.items?.length) {
          const mapped = await Promise.all(
            data.items.map(async (item, idx) => {
              let productList = [];
              if (item.product_category_id) {
                try {
                  const r = await stockReturnApi.getProductReport({
                    cursor: 0,
                    limit: 200,
                    filters: { search_text: '', start_date: '', end_date: '' },
                    category_id: item.product_category_id
                  });
                  productList = r.data?.results?.data || r.data?.results || [];
                } catch (_) {
                  /* ignore */
                }
              }
              const prod = productList.find(p => p.id === item.product_id);
              const opts = prod ? buildOptions(prod) : [];
              let lotsList = [];
              if (item.product_id) {
                try {
                  const lotRes = await stockReturnApi.getProductLots(
                    item.product_id
                  );
                  lotsList = lotRes.data?.results || [];
                } catch (_) {
                  /* ignore */
                }
              }
              return {
                tempId: Date.now() + idx,
                productCategoryId: item.product_category_id || '',
                productId: item.product_id || '',
                optionId: item.option_id || '',
                sku: item.sku || '',
                lotId: item.lot_id || '',
                availableQty: Number(item.available_qty) || '',
                returnQty: Number(item.return_qty) || '',
                uom: item.uom || '',
                pricePerUnit: Number(item.price_per_unit) || '',
                stockValue: Number(item.stock_value) || 0,
                productList,
                optionsList: opts,
                lotsList
              };
            })
          );
          setItems(mapped);
        }
      } catch (err) {
        console.error('Failed to load stock return:', err);
        toast.error('Failed to load stock return details');
      } finally {
        setLoading(false);
      }
    };
    fetchRecord();
  }, [id, isEditMode]);

  // ── helpers ───────────────────────────────────────────────────────────────────
  const buildOptions = prod => {
    let opts = prod.options;
    if (typeof opts === 'string') {
      try {
        opts = JSON.parse(opts);
      } catch (_) {
        console.error(opts);
      }
    }
    if (!opts || typeof opts !== 'object') return [];
    return Object.entries(opts).map(([key, val]) => ({
      value: key,
      label:
        val && typeof val === 'object'
          ? val.option_name || val.name || val.label || key
          : String(val || key)
    }));
  };

  // ── item handlers ─────────────────────────────────────────────────────────────
  const handleAddItem = () => setItems(prev => [...prev, newItem()]);

  const handleRemoveItem = idx => {
    if (items.length === 1) {
      toast.warning('At least one item is required');
      return;
    }
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCategoryChange = async (idx, catId) => {
    const updated = [...items];
    updated[idx] = {
      ...updated[idx],
      productCategoryId: catId,
      productId: '',
      optionId: '',
      sku: '',
      lotId: '',
      availableQty: '',
      returnQty: '',
      uom: '',
      pricePerUnit: '',
      stockValue: 0,
      productList: [],
      optionsList: []
    };
    setItems(updated);
    if (!catId) return;
    try {
      const r = await stockReturnApi.getProductReport({
        cursor: 0,
        limit: 200,
        filters: { search_text: '', start_date: '', end_date: '' },
        category_id: catId
      });
      const list = r.data?.results?.data || r.data?.results || [];
      setItems(prev => {
        const u = [...prev];
        u[idx] = { ...u[idx], productList: list };
        return u;
      });
    } catch (err) {
      toast.error('Failed to load products');
    }
  };

  const handleProductChange = async (idx, prodId) => {
    const updated = [...items];
    const prod = updated[idx].productList.find(p => p.id === prodId) || {};
    const opts = buildOptions(prod);
    const hasOpts = opts.length > 0;
    const price = hasOpts ? '' : Number(prod.base_price || prod.price) || '';
    const sku = prod.SKU || prod.sku || prod.product_sku || '';
    updated[idx] = {
      ...updated[idx],
      productId: prodId,
      optionId: '',
      sku: sku,
      lotId: '',
      availableQty: '',
      returnQty: '',
      uom: prod.uom_name || prod.unit || prod.unit_of_measurement || '',
      pricePerUnit: price,
      stockValue: 0,
      optionsList: opts,
      lotsList: []
    };
    setItems(updated);

    if (!prodId) return;

    try {
      const res = await stockReturnApi.getProductLots(prodId);
      const lots = res.data?.results || [];
      setItems(prev => {
        const u = [...prev];
        if (u[idx]) {
          u[idx] = { ...u[idx], lotsList: lots };
        }
        return u;
      });
    } catch (err) {
      console.error('Failed to fetch lots:', err);
      toast.error('Failed to load available lots for the product');
    }
  };

  const handleOptionChange = (idx, optId) => {
    const updated = [...items];
    const prod =
      updated[idx].productList.find(p => p.id === updated[idx].productId) || {};
    let prices = prod.option_prices;
    if (typeof prices === 'string') {
      try {
        prices = JSON.parse(prices);
      } catch (error) {
        console.error(error);
      }
    }
    const price =
      prices && prices[optId] !== undefined
        ? Number(prices[optId]) || ''
        : Number(prod.base_price) || '';
    const qty = Number(updated[idx].returnQty) || 0;
    updated[idx] = {
      ...updated[idx],
      optionId: optId,
      pricePerUnit: price,
      stockValue: price * qty
    };
    setItems(updated);
  };

  const handleLotChange = (idx, lotId) => {
    const updated = [...items];
    const lot = updated[idx].lotsList.find(l => l.lot_id === lotId) || {};
    const availableQty =
      lot.transferred_qty !== undefined ? Number(lot.transferred_qty) : '';
    const pricePerUnit =
      lot.price_per_unit !== undefined ? Number(lot.price_per_unit) : '';
    const returnQty = Number(updated[idx].returnQty) || 0;
    const sku = lot.sku || updated[idx].sku || '';
    updated[idx] = {
      ...updated[idx],
      lotId: lotId,
      sku: sku,
      availableQty: availableQty,
      pricePerUnit: pricePerUnit,
      stockValue: Number(pricePerUnit) * returnQty
    };
    setItems(updated);
  };

  const handleNumericChange = (idx, field, value) => {
    const updated = [...items];
    updated[idx][field] = value === '' ? '' : Number(value);
    const price = Number(updated[idx].pricePerUnit) || 0;
    const qty = Number(updated[idx].returnQty) || 0;
    updated[idx].stockValue = price * qty;
    setItems(updated);
  };

  const handleTextChange = (idx, field, value) => {
    const updated = [...items];
    updated[idx][field] = value;
    setItems(updated);
  };

  // ── totals ────────────────────────────────────────────────────────────────────
  const validItems = items.filter(i => i.productCategoryId && i.productId);
  const totalQty = items.reduce((s, i) => s + (Number(i.returnQty) || 0), 0);
  const subtotal = items.reduce((s, i) => s + (Number(i.stockValue) || 0), 0);
  const taxAmt = (subtotal * (Number(taxPercent) || 0)) / 100;
  const totalValue = subtotal + taxAmt;

  // ── submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async submitStatus => {
    if (!warehouseId) {
      toast.error('Warehouse is required');
      return;
    }
    const readyItems = validItems.filter(i => Number(i.returnQty) > 0);
    if (!readyItems.length) {
      toast.error('Add at least one item with Return Qty > 0');
      return;
    }
    setSubmitting(true);
    const payload = {
      return_date: returnDate,
      warehouse_id: warehouseId,
      po_invoice_no: poInvoiceNo || null,
      supplier_id: supplierId || null,
      customer_id: customerId || null,
      ref_no: refNo || null,
      notes: notes || null,
      tax_percent: Number(taxPercent) || 0,
      status: submitStatus || status,
      items: readyItems.map(i => ({
        product_category_id: i.productCategoryId,
        product_id: i.productId,
        option_id: i.optionId || null,
        sku: i.sku || null,
        lot_id: i.lotId || null,
        available_qty: Number(i.availableQty) || 0,
        return_qty: Number(i.returnQty) || 0,
        uom: i.uom || null,
        price_per_unit: Number(i.pricePerUnit) || 0,
        stock_value: Number(i.stockValue) || 0
      }))
    };
    try {
      const res = isEditMode
        ? await stockReturnApi.updateStockReturn(id, payload)
        : await stockReturnApi.createStockReturn(payload);
      if (res.data?.success || res.status === 201 || res.status === 200) {
        toast.success(res.data?.results || 'Stock Return saved!');
        navigate(`/${tenant}/stock-return`);
      } else {
        toast.error(res.data?.results || 'Failed to save Stock Return');
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail || err.message || 'Error occurred'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  const inputStyle = {
    borderRadius: '8px',
    padding: '9px 13px',
    border: '1.5px solid #D8E0F0',
    fontSize: '13px'
  };

  return (
    <Container
      fluid
      className="py-4 px-4"
      style={{ backgroundColor: '#F4F6FB', minHeight: '100vh' }}
    >
      {/* ── breadcrumb + header ── */}
      <div className="mb-3" style={{ fontSize: '12px', color: '#6E7D96' }}>
        Returns&nbsp;/&nbsp;
        <span className="text-dark fw-semibold">
          {isEditMode ? 'Edit Return Form' : 'New Return Form'}
        </span>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '22px' }}>
          Stock Return
          {isEditMode && stockReturnCode && (
            <span
              className="ms-3 text-muted"
              style={{ fontSize: '14px', fontWeight: 500 }}
            >
              #{stockReturnCode}
            </span>
          )}
        </h4>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            className="rounded-pill px-4"
            style={{ fontSize: '13px' }}
            disabled={submitting}
            onClick={() => navigate(`/${tenant}/stock-return`)}
          >
            Discard
          </Button>
          <Button
            variant="primary"
            className="rounded-pill px-4 fw-semibold"
            style={{ fontSize: '13px' }}
            disabled={submitting}
            onClick={() => handleSubmit('draft')}
          >
            {submitting ? 'Saving...' : 'Save Return'}
          </Button>
        </div>
      </div>

      {loading ? (
        <Card
          className="shadow-sm border-0 py-5 text-center"
          style={{ borderRadius: '14px' }}
        >
          <Card.Body>
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 text-muted">Loading...</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* ── HEADER CARD ── */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: '14px' }}
          >
            <Card.Body className="p-4">
              <Row className="g-3">
                {/* Stock Return ID */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: '12px' }}
                    >
                      Stock Return ID
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={
                        nextCodeLoading
                          ? 'Generating…'
                          : stockReturnCode ||
                            (isEditMode ? '' : 'Pending save')
                      }
                      disabled
                      style={{
                        ...inputStyle,
                        backgroundColor: '#F8FAFC',
                        color:
                          stockReturnCode && !nextCodeLoading
                            ? '#1E293B'
                            : '#94A3B8',
                        fontWeight:
                          stockReturnCode && !nextCodeLoading ? 600 : 400,
                        letterSpacing: stockReturnCode ? '0.5px' : 'normal'
                      }}
                    />
                    {!isEditMode && stockReturnCode && !nextCodeLoading && (
                      <div
                        className="text-muted mt-1"
                        style={{ fontSize: '11px' }}
                      >
                        Preview — confirmed on save
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* Date */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: '12px' }}
                    >
                      Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={returnDate}
                      onChange={e => setReturnDate(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Warehouse */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: '12px' }}
                    >
                      Warehouse <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={warehouseId}
                      onChange={e => setWarehouseId(e.target.value)}
                      style={inputStyle}
                      required
                    >
                      <option value="">Select warehouse…</option>
                      {warehouses.map(w => (
                        <option key={w.id || w.value} value={w.id || w.value}>
                          {w.name || w.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* PO / Invoice No */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: '12px' }}
                    >
                      PO/Invoice No
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. INV-0029-23"
                      value={poInvoiceNo}
                      onChange={e => setPoInvoiceNo(e.target.value)}
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                {/* Supplier */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: '12px' }}
                    >
                      Supplier
                    </Form.Label>
                    <Form.Select
                      value={supplierId}
                      onChange={e => setSupplierId(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Select supplier…</option>
                      {suppliers.map(s => (
                        <option
                          key={s.supplier_id || s.id || s.value}
                          value={s.supplier_id || s.id || s.value}
                        >
                          {s.supplier_name || s.name || s.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Reference Number */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: '12px' }}
                    >
                      Reference Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter reference…"
                      value={refNo}
                      onChange={e => setRefNo(e.target.value)}
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>

                {/* Customer */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold text-secondary mb-1"
                      style={{ fontSize: '12px' }}
                    >
                      Customer
                    </Form.Label>
                    <Form.Select
                      value={customerId}
                      onChange={e => setCustomerId(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Select customer…</option>
                      {customers.map(c => (
                        <option
                          key={c.customer_id || c.id || c.value}
                          value={c.customer_id || c.id || c.value}
                        >
                          {c.customer_name || c.name || c.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ── PRODUCT DETAILS CARD ── */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: '14px' }}
          >
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5
                  className="fw-bold mb-0"
                  style={{ fontSize: '15px', color: '#1E293B' }}
                >
                  Product Details
                </h5>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="rounded-pill d-flex align-items-center gap-1"
                  style={{ fontSize: '12px', padding: '5px 14px' }}
                  onClick={handleAddItem}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Product
                </Button>
              </div>

              <div className="table-responsive">
                <Table
                  className="align-middle mb-3"
                  style={{ fontSize: '12.5px' }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#F8FAFC' }}>
                      <th
                        className="text-secondary fw-semibold py-3"
                        style={{ width: '40px' }}
                      >
                        S.No
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3"
                        style={{ minWidth: '130px' }}
                      >
                        Category
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3"
                        style={{ minWidth: '160px' }}
                      >
                        Product
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3"
                        style={{ minWidth: '120px' }}
                      >
                        Option
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3"
                        style={{ minWidth: '100px' }}
                      >
                        SKU
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3"
                        style={{ minWidth: '110px' }}
                      >
                        LOT ID
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3 text-end"
                        style={{ minWidth: '90px' }}
                      >
                        Available
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3 text-center"
                        style={{ minWidth: '100px' }}
                      >
                        Return Qty
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3"
                        style={{ minWidth: '70px' }}
                      >
                        UoM
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3 text-end"
                        style={{ minWidth: '110px' }}
                      >
                        Price/Unit
                      </th>
                      <th
                        className="text-secondary fw-semibold py-3 text-end"
                        style={{ minWidth: '120px' }}
                      >
                        Stock Value
                      </th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr
                        key={item.tempId}
                        style={{ borderBottom: '1px solid #F1F5F9' }}
                      >
                        {/* S.No */}
                        <td className="text-secondary fw-semibold">
                          {idx + 1}
                        </td>

                        {/* Category */}
                        <td>
                          <Form.Select
                            value={item.productCategoryId}
                            onChange={e =>
                              handleCategoryChange(idx, e.target.value)
                            }
                            style={{ ...inputStyle, minWidth: '120px' }}
                          >
                            <option value="">Category</option>
                            {categories.map(c => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Form.Select>
                          {item.productCategoryId && (
                            <Badge
                              bg={catColour(item.productCategoryId)}
                              className="mt-1"
                              style={{ fontSize: '10px' }}
                            >
                              {categories.find(
                                c => c.value === item.productCategoryId
                              )?.label || ''}
                            </Badge>
                          )}
                        </td>

                        {/* Product */}
                        <td>
                          <Form.Select
                            value={item.productId}
                            onChange={e =>
                              handleProductChange(idx, e.target.value)
                            }
                            disabled={!item.productCategoryId}
                            style={{ ...inputStyle, minWidth: '150px' }}
                          >
                            <option value="">Select product</option>
                            {item.productList.map(p => (
                              <option key={p.id} value={p.id}>
                                {p['Product Name'] || p.product_name || p.name}
                              </option>
                            ))}
                          </Form.Select>
                        </td>

                        {/* Option */}
                        <td>
                          <Form.Select
                            value={item.optionId}
                            onChange={e =>
                              handleOptionChange(idx, e.target.value)
                            }
                            disabled={
                              !item.productId || item.optionsList.length === 0
                            }
                            style={{ ...inputStyle, minWidth: '110px' }}
                          >
                            <option value="">None</option>
                            {item.optionsList.map(o => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Form.Select>
                          {item.optionId && (
                            <Badge
                              bg="light"
                              text="dark"
                              className="mt-1 border"
                              style={{ fontSize: '10px' }}
                            >
                              {item.optionsList.find(
                                o => o.value === item.optionId
                              )?.label || item.optionId}
                            </Badge>
                          )}
                        </td>

                        {/* SKU */}
                        <td>
                          <Form.Control
                            type="text"
                            placeholder="SKU"
                            value={item.sku}
                            onChange={e =>
                              handleTextChange(idx, 'sku', e.target.value)
                            }
                            disabled={!item.productId}
                            style={inputStyle}
                          />
                        </td>

                        {/* LOT ID */}
                        <td>
                          <Form.Select
                            value={item.lotId}
                            onChange={e => handleLotChange(idx, e.target.value)}
                            disabled={!item.productId}
                            style={{ ...inputStyle, minWidth: '130px' }}
                          >
                            <option value="">Select Lot</option>
                            {item.lotsList &&
                              item.lotsList.map(l => (
                                <option key={l.lot_id} value={l.lot_id}>
                                  {l.lot_id} ({l.transferred_qty || 0})
                                </option>
                              ))}
                          </Form.Select>
                        </td>

                        {/* Available */}
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            placeholder="0"
                            value={item.availableQty}
                            onChange={e =>
                              handleNumericChange(
                                idx,
                                'availableQty',
                                e.target.value
                              )
                            }
                            disabled={!item.productId}
                            style={{ ...inputStyle, textAlign: 'right' }}
                          />
                        </td>

                        {/* Return Qty */}
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            placeholder="Qty"
                            value={item.returnQty}
                            onChange={e =>
                              handleNumericChange(
                                idx,
                                'returnQty',
                                e.target.value
                              )
                            }
                            disabled={!item.productId}
                            style={{
                              ...inputStyle,
                              textAlign: 'center',
                              border: '1.5px solid #6366F1',
                              fontWeight: 600
                            }}
                          />
                        </td>

                        {/* UoM */}
                        <td>
                          <Form.Control
                            type="text"
                            placeholder="Unit"
                            value={item.uom}
                            onChange={e =>
                              handleTextChange(idx, 'uom', e.target.value)
                            }
                            disabled={!item.productId}
                            style={inputStyle}
                          />
                        </td>

                        {/* Price/Unit */}
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            placeholder="Price"
                            value={item.pricePerUnit}
                            onChange={e =>
                              handleNumericChange(
                                idx,
                                'pricePerUnit',
                                e.target.value
                              )
                            }
                            disabled={!item.productId}
                            style={{ ...inputStyle, textAlign: 'right' }}
                          />
                        </td>

                        {/* Stock Value */}
                        <td
                          className="text-end fw-bold"
                          style={{ color: '#1E293B', fontSize: '13px' }}
                        >
                          {Math.round(item.stockValue || 0).toLocaleString()}
                        </td>

                        {/* Delete */}
                        <td className="text-center">
                          <Button
                            variant="link"
                            className="p-0 text-danger border-0"
                            onClick={() => handleRemoveItem(idx)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                className="rounded-pill d-flex align-items-center gap-1 mt-1"
                style={{ fontSize: '12px', padding: '5px 14px' }}
                onClick={handleAddItem}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Another Product
              </Button>
            </Card.Body>
          </Card>

          {/* ── FOOTER: Notes + Summary ── */}
          <Row className="g-4">
            {/* Notes */}
            <Col lg={7}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{ borderRadius: '14px' }}
              >
                <Card.Body className="p-4">
                  <Form.Label
                    className="fw-semibold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Notes / Reasons for Return
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Specify damaged stock, expiry, or incorrect delivery…"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    style={{ ...inputStyle, resize: 'none' }}
                  />
                </Card.Body>
              </Card>
            </Col>

            {/* Summary */}
            <Col lg={5}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{
                  borderRadius: '14px',
                  backgroundColor: '#F8FAFF',
                  border: '1.5px solid #E8EDFF'
                }}
              >
                <Card.Body className="p-4">
                  {/* Subtotal */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span
                      className="text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Subtotal Items
                    </span>
                    <span className="fw-bold text-dark">
                      {validItems.length} Item
                      {validItems.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <hr style={{ borderTop: '1.5px dashed #D1D5DB' }} />

                  {/* Total */}
                  <div className="mb-1">
                    <div
                      className="text-secondary mb-1"
                      style={{
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Total Share in Value
                    </div>
                    <h3
                      className="fw-bold mb-0"
                      style={{ color: '#4F46E5', fontSize: '28px' }}
                    >
                      {totalValue.toLocaleString()} UGX
                    </h3>
                  </div>

                  <Button
                    variant="primary"
                    className="w-100 rounded-pill fw-semibold mt-4 py-2"
                    style={{
                      fontSize: '14px',
                      backgroundColor: '#4F46E5',
                      border: 'none'
                    }}
                    disabled={submitting}
                    onClick={() => handleSubmit('posted')}
                  >
                    {submitting ? 'Submitting…' : 'Complete Stock Return'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}
